import React, { ReactNode, useEffect, useState } from 'react';
import Box from 'ui-box';
import {
  Card,
  defaultFills,
  Dialog,
  Heading,
  Icon,
  majorScale,
  minorScale,
  Spinner,
} from '../../npaper';
import { CompanyThumbnail } from '../companies';
import { ContactThumbnail } from '../contacts';

import { animated, useSpring } from 'react-spring';

import { ShortCutTag } from './Navigation';
import { ICompany, IContact, IProduct, ISale, useStores } from '../../stores';
import { useDebounce } from 'use-debounce/lib';
import { SaleThumbnail } from '../sales';
import { Observer } from 'mobx-react-lite';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { SaleTypeTitle } from '../sales/SaleTypeTitle';

const AnimateIn = ({
  children,
  delay,
}: {
  children: ReactNode;
  delay?: number;
}) => {
  const props = useSpring({
    delay,
    opacity: 1,
    transform: 'scale(1)',
    from: {
      opacity: 0,
      transform: 'scale(0.95)',
    },
  });
  return <animated.div style={props}>{children}</animated.div>;
};

export const LookupDialog = ({
  isShown,
  onClose,
}: {
  isShown: boolean;
  onClose: () => void;
}) => {
  const history = useHistory();

  const { contactStore, companyStore, productStore, saleStore } = useStores();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 200, { leading: true });
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const [contacts, setContacts] = useState<IContact[]>();
  const [companies, setCompanies] = useState<ICompany[]>();
  const [products, setProducts] = useState<IProduct[]>();
  const [sales, setSales] = useState<ISale[]>();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isSub = true;
    setSelectedIndex(undefined);
    if (debouncedQuery) {
      const filter = { filter: { search: debouncedQuery } };
      setIsLoading(true);
      Promise.all([
        contactStore.fetchMore(filter, MAX_LIMIT),
        companyStore.fetchMore(filter, MAX_LIMIT),
        productStore.fetchMore(filter, MAX_LIMIT),
        saleStore.fetchMore(filter, MAX_LIMIT),
      ]).then(([contacts, companies, products, sales]) => {
        if (isSub) {
          setContacts(contacts.items.slice(0, MAX_LIMIT));
          setCompanies(companies.items.slice(0, MAX_LIMIT));
          setProducts(products.items.slice(0, MAX_LIMIT));
          setSales(sales.items.slice(0, MAX_LIMIT));
          setIsLoading(false);
        }
      });
    } else {
      setContacts([]);
      setCompanies([]);
      setProducts([]);
      setSales([]);
    }
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const MAX_LIMIT = 5;

  const lengthAll = [
    contacts || [],
    companies || [],
    products || [],
    sales || [],
  ].reduce((p, n) => p + n.length, 0);

  return (
    <Dialog
      isShown={isShown}
      onClose={() => {
        setQuery('');
        onClose();
      }}
      onOpened={() => inputRef.current && inputRef.current.focus()}
    >
      <Box className="border-bottom bg-white flex items-center pl-2">
        <Icon icon="search" />
        <input
          ref={inputRef}
          className="border-none bg-white p-2 w-full text-lg outline-none placeholder-gray-400"
          type="text"
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSelectedIndex((i) => Math.max((i || 0) - 1, -1));
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSelectedIndex((i) =>
                Math.min(lengthAll - 1, (i === undefined ? -1 : i) + 1)
              );
            } else if (e.key === 'Enter') {
              // Compute the correct item to route to
              // TODO: Refactor this..
              if (!contacts || !companies || !sales || !products) return;
              if (!selectedIndex && selectedIndex !== 0) return;
              const items = [...contacts, ...companies, ...sales, ...products];
              const item = items[selectedIndex];

              const type =
                selectedIndex < contacts.length
                  ? 'contacts'
                  : selectedIndex - contacts.length < companies.length
                  ? 'companies'
                  : selectedIndex - contacts.length - companies.length <
                    sales.length
                  ? 'sales'
                  : 'products';

              history.push(`/${type}/${item.id}`);
              onClose();
            }
          }}
        />
      </Box>
      <Box
        maxHeight={query ? '400px' : '40px'}
        overflow="auto"
        transition="max-height 250ms"
      >
        {!query && (
          <Box
            padding={majorScale(1)}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box className="p medium">
              <ShortCutTag
                icon="arrow-up"
                paddingX={6}
                text=""
                marginRight={majorScale(1)}
              />
              <ShortCutTag
                icon="arrow-down"
                paddingX={6}
                text=""
                marginRight={majorScale(1)}
              />
              to navigate
            </Box>
            <Box className="p medium">
              <ShortCutTag
                text="ENTER"
                paddingX={6}
                marginRight={majorScale(1)}
              />
              to select
            </Box>
            <Box className="p medium">
              <ShortCutTag
                text="ESCAPE"
                paddingX={6}
                marginRight={majorScale(1)}
              />
              to close
            </Box>
          </Box>
        )}
        {query && !isLoading && lengthAll === 0 && (
          <Box padding={majorScale(1)} textAlign="center">
            <p className="medium">No results found</p>
          </Box>
        )}
        {isLoading && (
          <Box padding={majorScale(1)} textAlign="center">
            <Spinner />
          </Box>
        )}
        {query && !isLoading && (
          <>
            {contacts && contacts.length > 0 && (
              <Box padding={majorScale(1)} className="border-bottom">
                <Heading size={200}>Contacts</Heading>
                {contacts.map((contact, i) => (
                  <AnimateIn key={contact.id} delay={i * 50}>
                    <Card
                      marginTop={minorScale(1)}
                      paddingX={majorScale(1)}
                      paddingY={minorScale(1)}
                      backgroundColor="white"
                      interactive
                      display="flex"
                      alignItems="center"
                      borderColor={
                        selectedIndex === i ? defaultFills.info[500] : undefined
                      }
                      onClick={() => {
                        history.push(`/contacts/${contact.id}`);
                        onClose();
                      }}
                    >
                      <ContactThumbnail
                        showName={false}
                        marginRight={majorScale(1)}
                      />
                      <Box>
                        <p className="medium">{contact.name}</p>
                        <p className="muted small">
                          {contact.emails.join(', ')}
                        </p>
                      </Box>
                    </Card>
                  </AnimateIn>
                ))}
              </Box>
            )}

            {companies && companies.length > 0 && (
              <Box padding={majorScale(1)} className="border-bottom">
                <Heading size={200}>Companies</Heading>
                {companies.map((company, i) => (
                  <AnimateIn key={company.id} delay={i * 50}>
                    <Card
                      marginTop={minorScale(1)}
                      padding={minorScale(1)}
                      backgroundColor="white"
                      interactive
                      display="flex"
                      alignItems="center"
                      borderColor={
                        selectedIndex === i + (contacts || []).length
                          ? defaultFills.info[500]
                          : undefined
                      }
                      onClick={() => {
                        history.push(`/companies/${company.id}`);
                        onClose();
                      }}
                    >
                      <CompanyThumbnail
                        company={company}
                        showName={false}
                        marginRight={majorScale(1)}
                      />

                      <p className="medium">{company.name}</p>
                    </Card>
                  </AnimateIn>
                ))}
              </Box>
            )}

            {sales && sales.length > 0 && (
              <Box padding={majorScale(1)} className="border-bottom">
                <Heading size={200}>Sales</Heading>
                {sales.map((sale, i) => (
                  <AnimateIn key={sale.id} delay={i * 50}>
                    <Card
                      marginTop={minorScale(1)}
                      paddingX={majorScale(1)}
                      paddingY={minorScale(1)}
                      backgroundColor="white"
                      interactive
                      display="flex"
                      alignItems="center"
                      borderColor={
                        selectedIndex ===
                        i + (contacts || []).length + (companies || []).length
                          ? defaultFills.info[500]
                          : undefined
                      }
                      onClick={() => {
                        history.push(`/sales/${sale.id}`);
                        onClose();
                      }}
                    >
                      <SaleThumbnail
                        sale={sale}
                        showNumber={false}
                        marginRight={majorScale(1)}
                      />
                      <div>
                        <p className="medium">{sale.title}</p>
                        <p className="small muted">
                          <Observer>
                            {() => (
                              <span>
                                <Box is="span" textTransform="capitalize">
                                  <SaleTypeTitle sale={sale} />
                                </Box>
                                {' - '}
                                {sale.customer ? (
                                  sale.customer.name
                                ) : (
                                  <Spinner size={16} />
                                )}
                              </span>
                            )}
                          </Observer>
                        </p>
                      </div>
                      <Box marginLeft="auto">
                        <span className="font-mono">
                          <FormattedNumber
                            format="currency"
                            value={sale.sub_total}
                          />
                        </span>
                      </Box>
                    </Card>
                  </AnimateIn>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};
